/* Typed doors into the app's untyped JavaScript hooks (src/hooks/*.js, src/ui/hooks), for the
   TypeScript files UND-1133 converts. Each type is the narrow shape those files use; `any` appears
   only where the JavaScript module hands back data the API defines. */
import * as hooks from '../hooks';

export type AuthUser = {
  userId?: string;
  email?: string;
  roles?: string[];
  isInternal?: boolean;
  isAdmin?: boolean;
  isUnderwriter?: boolean;
  isPolicyAnalyst?: boolean;
};

export type Auth = { loggedIn?: boolean; user?: AuthUser | null; logout: () => void };

export type NavigateLocation = { pathname: string; search?: string; state?: unknown; replace?: boolean };

export type Navigate = {
  to: (pathnameOrLocation: string | NavigateLocation) => void;
  back: (pathname?: string) => void;
  search: (queryString?: string, replace?: boolean) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useApi.jsx returns API payloads
export type ApiMethod = (...args: any[]) => Promise<any>;
/* Every name is a method (useApi.jsx defines hundreds); typed as an index the compiler does not
   treat as possibly missing, since a misspelt method fails the integration suite's unmocked check. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useApi.jsx
export type Api = { [method: string]: ApiMethod } & any;

export type AsyncState<T> = {
  value: T | undefined;
  status: 'idle' | 'pending' | 'success' | 'error';
  execute: (...args: unknown[]) => Promise<unknown>;
};

export type AppSettings = { current: Record<string, unknown> };
export type AppState = { settings?: AppSettings; saveSettings?: (next: Record<string, unknown>, global?: boolean) => Promise<void> };

/* Each reads the hook off the module when called, not at import, so a test that mocks src/hooks
   with only the hooks it needs still loads this file. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: src/hooks/index.js
const js = hooks as any;

export const useAuth = (): Auth => js.useAuth();
export const useNavigate = (): Navigate => js.useNavigate();
export const useApi = (): Api => js.useApi();
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useAsync.js takes any async function
export const useAsync = <T>(fn: (...args: any[]) => Promise<T>, options?: { immediate?: boolean }): AsyncState<T> => js.useAsync(fn, options);
export const useAppState = (): [AppState, (update: unknown) => void] => js.useAppState();
